import React from "react";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { PersonelPicker } from "./PersonelPicker";

const refs = (j) => (j.personel_ref && j.personel_ref.length ? j.personel_ref : (j.personel || []).map((n) => ({ nama: n, jemaat_id: null })));

const nid = () => Math.random().toString(36).slice(2, 10);
const baruJabatan = () => ({ id: nid(), nama: "", personel: [] });
const baruUnit = () => ({ id: nid(), nama: "", jabatan: [], keterangan: [], sub_unit: [] });
const lines = (arr) => (arr || []).join("\n");
const splitLines = (v) => v.split("\n");
const trimLines = (arr) => (arr || []).map((s) => s.trim()).filter(Boolean);
const replaceById = (arr, id, item) => arr.map((x) => (x.id === id ? item : x));
const removeById = (arr, id) => arr.filter((x) => x.id !== id);
const moveItem = (arr, i, d) => { const j = i + d; if (j < 0 || j >= arr.length) return arr; const c = [...arr]; [c[i], c[j]] = [c[j], c[i]]; return c; };

function Hapus({ onClick, testId, label = "Hapus" }) {
  return <button type="button" onClick={onClick} className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 shrink-0" title={label} aria-label={label} data-testid={testId}><Trash2 className="h-4 w-4" /></button>;
}

function JabatanRow({ j, onChange, onHapus, prefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.4fr_auto] gap-2 items-start" data-testid={`${prefix}-jabatan-${j.id}`}>
      <Input value={j.nama} onChange={(e) => onChange({ ...j, nama: e.target.value })} placeholder="Nama jabatan" className="min-h-[40px] rounded-lg text-sm" data-testid={`${prefix}-jabatan-nama-${j.id}`} />
      <PersonelPicker value={refs(j)} onChange={(r) => onChange({ ...j, personel_ref: r, personel: r.map((p) => p.nama) })} testId={`${prefix}-jabatan-personel-${j.id}`} />
      <Hapus onClick={onHapus} testId={`${prefix}-jabatan-hapus-${j.id}`} label="Hapus jabatan" />
    </div>
  );
}

function JabatanEditor({ list, onChange, prefix }) {
  return (
    <div className="space-y-2">
      {list.map((j) => <JabatanRow key={j.id} j={j} prefix={prefix} onChange={(nj) => onChange(replaceById(list, j.id, nj))} onHapus={() => onChange(removeById(list, j.id))} />)}
      <Button type="button" size="sm" variant="outline" className="rounded-lg h-8 text-xs" onClick={() => onChange([...list, baruJabatan()])} data-testid={`${prefix}-jabatan-tambah`}><Plus className="h-3 w-3 mr-1" /> Jabatan</Button>
    </div>
  );
}

function SubUnitEditor({ u, onChange, onHapus, prefix }) {
  return (
    <div className="rounded-xl border p-3 space-y-3 border-emerald-200 bg-emerald-50/30" data-testid={`${prefix}-unit-${u.id}`}>
      <div className="flex items-center gap-2">
        <Input value={u.nama} onChange={(e) => onChange({ ...u, nama: e.target.value })} placeholder="Nama sub-unit / komisi / departemen" className="min-h-[40px] rounded-lg font-semibold" data-testid={`${prefix}-unit-nama-${u.id}`} />
        <Hapus onClick={onHapus} testId={`${prefix}-unit-hapus-${u.id}`} label="Hapus sub-unit" />
      </div>
      <div>
        <div className="text-xs font-semibold text-slate-500 mb-1">Jabatan & Personel</div>
        <JabatanEditor list={u.jabatan || []} onChange={(l) => onChange({ ...u, jabatan: l })} prefix={`${prefix}-unit-${u.id}`} />
      </div>
      <div>
        <div className="text-xs font-semibold text-slate-500 mb-1">Keterangan / Program (opsional, satu per baris)</div>
        <Textarea rows={1} value={lines(u.keterangan)} onChange={(e) => onChange({ ...u, keterangan: splitLines(e.target.value) })} onBlur={() => onChange({ ...u, keterangan: trimLines(u.keterangan) })} className="text-sm rounded-lg min-h-[40px]" data-testid={`${prefix}-unit-keterangan-${u.id}`} />
      </div>
    </div>
  );
}

function UnitEditor({ u, onChange, onHapus, prefix }) {
  const subs = u.sub_unit || [];
  return (
    <div className="rounded-xl border p-3 space-y-3 border-slate-300 bg-white" data-testid={`${prefix}-unit-${u.id}`}>
      <div className="flex items-center gap-2">
        <Input value={u.nama} onChange={(e) => onChange({ ...u, nama: e.target.value })} placeholder="Nama unit (mis. Majelis Penatalayanan)" className="min-h-[40px] rounded-lg font-semibold" data-testid={`${prefix}-unit-nama-${u.id}`} />
        <Hapus onClick={onHapus} testId={`${prefix}-unit-hapus-${u.id}`} label="Hapus unit" />
      </div>
      <div>
        <div className="text-xs font-semibold text-slate-500 mb-1">Jabatan & Personel<span className="font-normal"> (jabatan pertama = pimpinan unit)</span></div>
        <JabatanEditor list={u.jabatan || []} onChange={(l) => onChange({ ...u, jabatan: l })} prefix={`${prefix}-unit-${u.id}`} />
      </div>
      <div>
        <div className="text-xs font-semibold text-slate-500 mb-1">Keterangan / Program (opsional, satu per baris)</div>
        <Textarea rows={1} value={lines(u.keterangan)} onChange={(e) => onChange({ ...u, keterangan: splitLines(e.target.value) })} onBlur={() => onChange({ ...u, keterangan: trimLines(u.keterangan) })} className="text-sm rounded-lg min-h-[40px]" data-testid={`${prefix}-unit-keterangan-${u.id}`} />
      </div>
      {(
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-1">Sub-unit / Komisi / Departemen</div>
          <div className="space-y-2">
            {subs.map((s) => <SubUnitEditor key={s.id} u={s} prefix={prefix} onChange={(ns) => onChange({ ...u, sub_unit: replaceById(subs, s.id, ns) })} onHapus={() => onChange({ ...u, sub_unit: removeById(subs, s.id) })} />)}
            <Button type="button" size="sm" variant="outline" className="rounded-lg h-8 text-xs" onClick={() => onChange({ ...u, sub_unit: [...subs, baruUnit()] })} data-testid={`${prefix}-unit-${u.id}-subunit-tambah`}><Plus className="h-3 w-3 mr-1" /> Sub-unit</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function BidangEditor({ b, index, total, onChange, onMove, onHapus }) {
  const units = b.unit || [];
  const prefix = `editor-bidang-${b.id}`;
  return (
    <Card className="p-4 rounded-2xl space-y-3" data-testid={prefix}>
      <div className="flex items-center gap-2">
        <Input value={b.nama} onChange={(e) => onChange({ ...b, nama: e.target.value })} placeholder="Nama bidang" className="min-h-[44px] rounded-lg font-bold text-base" data-testid={`${prefix}-nama`} />
        <Button type="button" size="icon" variant="ghost" className="h-9 w-9" disabled={index === 0} onClick={() => onMove(-1)} aria-label="Naik"><ChevronUp className="h-4 w-4" /></Button>
        <Button type="button" size="icon" variant="ghost" className="h-9 w-9" disabled={index === total - 1} onClick={() => onMove(1)} aria-label="Turun"><ChevronDown className="h-4 w-4" /></Button>
        <Hapus onClick={onHapus} testId={`${prefix}-hapus`} label="Hapus bidang" />
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        {units.map((u) => <UnitEditor key={u.id} u={u} prefix={prefix} onChange={(nu) => onChange({ ...b, unit: replaceById(units, u.id, nu) })} onHapus={() => window.confirm(`Hapus unit "${u.nama}"?`) && onChange({ ...b, unit: removeById(units, u.id) })} />)}
      </div>
      <Button type="button" size="sm" variant="outline" className="rounded-lg" onClick={() => onChange({ ...b, unit: [...units, baruUnit()] })} data-testid={`${prefix}-unit-tambah`}><Plus className="h-4 w-4 mr-1" /> Tambah Unit / Majelis</Button>
    </Card>
  );
}

export function EditorStruktur({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const bidang = data.bidang || [];
  return (
    <div className="space-y-4" data-testid="struktur-editor">
      <Card className="p-4 rounded-2xl space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <div><div className="text-xs font-semibold text-slate-500 mb-1">Nama Periode</div><Input value={data.nama} onChange={(e) => set("nama", e.target.value)} className="min-h-[40px] rounded-lg" data-testid="editor-periode-nama" /></div>
          <div><div className="text-xs font-semibold text-slate-500 mb-1">Tahun Mulai</div><Input type="number" value={data.tahun_mulai} onChange={(e) => set("tahun_mulai", Number(e.target.value))} className="min-h-[40px] rounded-lg" data-testid="editor-tahun-mulai" /></div>
          <div><div className="text-xs font-semibold text-slate-500 mb-1">Tahun Selesai</div><Input type="number" value={data.tahun_selesai} onChange={(e) => set("tahun_selesai", Number(e.target.value))} className="min-h-[40px] rounded-lg" data-testid="editor-tahun-selesai" /></div>
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-800 mb-2">Pimpinan Jemaat</div>
          <JabatanEditor list={data.pimpinan || []} onChange={(l) => set("pimpinan", l)} prefix="editor-pimpinan" />
        </div>
      </Card>
      {bidang.map((b, i) => (
        <BidangEditor key={b.id} b={b} index={i} total={bidang.length} onChange={(nb) => set("bidang", replaceById(bidang, b.id, nb))} onMove={(d) => set("bidang", moveItem(bidang, i, d))} onHapus={() => window.confirm(`Hapus bidang "${b.nama}" beserta seluruh unitnya?`) && set("bidang", removeById(bidang, b.id))} />
      ))}
      <Button type="button" variant="outline" className="rounded-xl" onClick={() => set("bidang", [...bidang, { id: nid(), nama: "", unit: [] }])} data-testid="editor-bidang-tambah"><Plus className="h-4 w-4 mr-1" /> Tambah Bidang</Button>
    </div>
  );
}
